<template>
  <div :class="['layout', roleClass]">
    <div class="topbar glass-card">
      <div class="topbar__left">
        <div class="brand">
          <div class="brand__dot" />
          <div class="brand__title">{{ $t('app.title') }}</div>
        </div>
      </div>

      <div class="topbar__right">
        <el-button type="primary" class="btn-glass" plain @click="toggleLang">
          {{ $t('common.language') }}: {{ langLabel }}
        </el-button>

        <el-badge v-if="role === 'USER'" :value="taskRedDotCount>9 ? '9+' : taskRedDotCount" :hidden="taskRedDotCount <= 0" class="badge badge-top">
          <el-button @click="goTasks">
            {{ $t('menu.tasks') }}
          </el-button>
        </el-badge>
        <el-badge v-if="role === 'SECONDARY_HANDLER'" :value="secondaryTaskBadge>9 ? '9+' : secondaryTaskBadge" :hidden="secondaryTaskBadge <= 0" class="badge badge-top">
          <el-button @click="goSecondaryTasks">
            {{ $t('menu.tasks') }}
          </el-button>
        </el-badge>

        <el-dropdown>
          <span class="user-pill">
            {{ user ? user.displayName : '-' }} <i class="el-icon-arrow-down" />
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item @click.native="goChangePassword">{{ $t('auth.changePassword') }}</el-dropdown-item>
            <el-dropdown-item @click.native="logout">{{ $t('common.logout') }}</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <div class="content">
      <div class="sidebar glass-card">
      <el-menu :default-active="$route.path" class="menu" router>
          <el-menu-item index="/dashboard">
            <i class="el-icon-menu" />
            <span slot="title">{{ $t('menu.dashboard') }}</span>
          </el-menu-item>

          <el-menu-item v-if="role==='USER'" index="/tickets">
            <i class="el-icon-document" />
            <span slot="title">{{ $t('menu.tickets') }}</span>
            <span v-if="ticketMenuBadge>0" class="menu-item-badge">{{ ticketMenuBadge>9 ? '9+' : ticketMenuBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='USER'" index="/tasks">
            <i class="el-icon-bell" />
            <span slot="title">{{ $t('menu.tasks') }}</span>
            <span v-if="taskRedDotCount>0" class="menu-item-badge">{{ taskRedDotCount>9 ? '9+' : taskRedDotCount }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='USER'" index="/user/announcements">
            <i class="el-icon-message" />
            <span slot="title">{{ $t('menu.announcements') }}</span>
            <span v-if="mustConfirmAnnouncement" class="menu-item-badge">1</span>
          </el-menu-item>

          <el-menu-item v-if="role==='HANDLER'" index="/handler/pool">
            <i class="el-icon-s-operation" />
            <span slot="title">{{ $t('menu.handlerPool') }}</span>
            <span v-if="handlerPoolBadge>0" class="menu-item-badge">{{ handlerPoolBadge>9 ? '9+' : handlerPoolBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='HANDLER'" index="/handler/in-progress">
            <i class="el-icon-loading" />
            <span slot="title">{{ $t('menu.handlerInProgress') }}</span>
            <span v-if="handlerInProgressReminderBadge>0" class="menu-item-badge">{{ handlerInProgressReminderBadge>9 ? '9+' : handlerInProgressReminderBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='HANDLER'" index="/handler/closed">
            <i class="el-icon-circle-check" />
            <span slot="title">{{ $t('menu.handlerClosed') }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='HANDLER'" index="/handler/disputes">
            <i class="el-icon-warning-outline" />
            <span slot="title">{{ $t('menu.handlerDisputes') }}</span>
            <span v-if="handlerDisputeBadge>0" class="menu-item-badge">{{ handlerDisputeBadge>9 ? '9+' : handlerDisputeBadge }}</span>
          </el-menu-item>

          <el-menu-item v-if="role==='SECONDARY_HANDLER'" index="/secondary/submit">
            <i class="el-icon-edit-outline" />
            <span slot="title">{{ $t('menu.tickets') }}</span>
            <span v-if="secondarySubmittedBadge>0" class="menu-item-badge">{{ secondarySubmittedBadge>9 ? '9+' : secondarySubmittedBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='SECONDARY_HANDLER'" index="/secondary/pending">
            <i class="el-icon-loading" />
            <span slot="title">{{ $t('menu.handlerInProgress') }}</span>
            <span v-if="secondaryWorkBadge>0" class="menu-item-badge">{{ secondaryWorkBadge>9 ? '9+' : secondaryWorkBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='SECONDARY_HANDLER'" index="/secondary/closed">
            <i class="el-icon-circle-check" />
            <span slot="title">{{ $t('menu.secondaryClosed') }}</span>
            <span v-if="secondaryClosedNoticeBadge>0" class="menu-item-badge">{{ secondaryClosedNoticeBadge>9 ? '9+' : secondaryClosedNoticeBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='SECONDARY_HANDLER'" index="/secondary/tasks">
            <i class="el-icon-bell" />
            <span slot="title">{{ $t('menu.tasks') }}</span>
            <span v-if="secondaryTaskBadge>0" class="menu-item-badge">{{ secondaryTaskBadge>9 ? '9+' : secondaryTaskBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='SECONDARY_HANDLER'" index="/secondary/announcements">
            <i class="el-icon-message" />
            <span slot="title">{{ $t('menu.announcements') }}</span>
            <span v-if="mustConfirmAnnouncement" class="menu-item-badge">1</span>
          </el-menu-item>

          <el-menu-item v-if="role==='ADMIN'" index="/kpi">
            <i class="el-icon-data-analysis" />
            <span slot="title">{{ $t('menu.kpi') }}</span>
          </el-menu-item>

          <el-menu-item v-if="role==='ADMIN'" index="/admin/users">
            <i class="el-icon-user" />
            <span slot="title">{{ $t('menu.adminUsers') }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='ADMIN'" index="/admin/tasks">
            <i class="el-icon-tickets" />
            <span slot="title">{{ $t('menu.adminTasks') }}</span>
            <span v-if="adminTaskBadge>0" class="menu-item-badge">{{ adminTaskBadge>9 ? '9+' : adminTaskBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='ADMIN'" index="/admin/announcements">
            <i class="el-icon-message" />
            <span slot="title">{{ $t('menu.adminAnnouncements') }}</span>
            <span v-if="adminAnnouncementBadge>0" class="menu-item-badge">{{ adminAnnouncementBadge>9 ? '9+' : adminAnnouncementBadge }}</span>
          </el-menu-item>
          <el-menu-item v-if="role==='ADMIN'" index="/admin/system">
            <i class="el-icon-setting" />
            <span slot="title">{{ $t('menu.adminSystem') }}</span>
          </el-menu-item>
        </el-menu>
      </div>

      <div class="main glass-card">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import { start as startStream, stop as stopStream } from '../services/stream';
import { updateAttention } from '../services/attention';

export default {
  name: 'MainLayout',
  data() {
    return {};
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    role() {
      return this.$store.getters.role;
    },
    mustConfirmAnnouncement() {
      return this.$store.state.mustConfirmAnnouncement;
    },
    taskRedDotCount() {
      return this.$store.state.taskRedDotCount;
    },
    ticketRedDotCount() {
      return this.$store.state.ticketRedDotCount || 0;
    },
    ticketNeedMoreCount() {
      return this.$store.state.ticketNeedMoreCount || 0;
    },
    ticketClosedNoticeCount() {
      return this.$store.state.ticketClosedNoticeCount || 0;
    },
    ticketMenuBadge() {
      // Prefer dedicated red-dot API; fall back to sum of need-more + closed-notice.
      const redDot = this.ticketRedDotCount || 0;
      if (redDot > 0) return redDot;
      return (this.ticketNeedMoreCount || 0) + (this.ticketClosedNoticeCount || 0);
    },
    adminAnnouncementBadge() {
      return this.$store.state.adminBadges ? this.$store.state.adminBadges.announcements : 0;
    },
    adminTaskBadge() {
      return this.$store.state.adminBadges ? this.$store.state.adminBadges.tasks : 0;
    },
    handlerPoolBadge() {
      const b = this.$store.state.handlerBadges || {};
      return Number(b.pool || 0) || Number(b.poolReminder || 0);
    },
    handlerInProgressReminderBadge() {
      return this.$store.state.handlerBadges ? this.$store.state.handlerBadges.inProgressReminder : 0;
    },
    handlerDisputeBadge() {
      const b = this.$store.state.handlerBadges || {};
      return Number(b.disputes || 0) || Number(b.disputeReminder || 0);
    },
    secondaryPendingBadge() {
      const b = this.$store.state.secondaryBadges || {};
      return Number(b.pendingCases || 0) || Number(b.pendingReminder || 0);
    },
    secondarySubmittedBadge() {
      const b = this.$store.state.secondaryBadges || {};
      return Number(b.submittedCases || 0) || Number(b.submittedReminder || 0);
    },
    secondaryClosedNoticeBadge() {
      return this.$store.state.secondaryBadges ? this.$store.state.secondaryBadges.closedNotice : 0;
    },
    secondaryDisputeBadge() {
      const b = this.$store.state.secondaryBadges || {};
      return Number(b.secondaryDisputes || 0) || Number(b.disputeReminder || 0);
    },
    secondaryTaskBadge() {
      return this.$store.state.secondaryBadges ? this.$store.state.secondaryBadges.taskRedDot : 0;
    },
    secondaryWorkBadge() {
      return (this.secondaryPendingBadge || 0) + (this.secondaryDisputeBadge || 0);
    },
    attentionCount() {
      if (this.role === 'USER') {
        return (this.mustConfirmAnnouncement ? 1 : 0) + (this.taskRedDotCount || 0) + (this.ticketMenuBadge || 0);
      }
      if (this.role === 'SECONDARY_HANDLER') {
        return (this.mustConfirmAnnouncement ? 1 : 0)
          + (this.secondaryWorkBadge || 0)
          + (this.secondarySubmittedBadge || 0)
          + (this.secondaryClosedNoticeBadge || 0)
          + (this.secondaryTaskBadge || 0);
      }
      if (this.role === 'ADMIN') {
        return (this.adminAnnouncementBadge || 0) + (this.adminTaskBadge || 0);
      }
      if (this.role === 'HANDLER') {
        return (this.handlerPoolBadge || 0) + (this.handlerInProgressReminderBadge || 0) + (this.handlerDisputeBadge || 0);
      }
      return 0;
    },
    lang() {
      return this.$i18n.locale;
    },
    langLabel() {
      return this.lang === 'zh' ? '中文' : 'EN';
    },
    roleClass() {
      if (this.role === 'ADMIN') return 'role-admin';
      if (this.role === 'HANDLER') return 'role-handler';
      if (this.role === 'SECONDARY_HANDLER') return 'role-secondary';
      if (this.role === 'USER') return 'role-user';
      return '';
    }
  },
  created() {
    // Router guard already ensures user is loaded via bootstrap
    // Just start SSE and update attention indicators
    startStream();
    updateAttention(this.attentionCount, this.$t('app.title'));
  },
  watch: {
    // Announcement is the only "hard interrupt" in the system: when it becomes required,
    // immediately force USER to the confirm page (no manual click / no need to wait for 423).
    mustConfirmAnnouncement(val) {
      if (!val) return;
      if (this.role && this.role !== 'USER' && this.role !== 'SECONDARY_HANDLER') return;
      if (this.$route.path === '/announcements/confirm') return;
      this.$store.commit('setAnnouncementReturnTo', this.$route.fullPath);
      this.$router.replace('/announcements/confirm');
    },
    attentionCount() {
      updateAttention(this.attentionCount, this.$t('app.title'));
    },
    lang() {
      updateAttention(this.attentionCount, this.$t('app.title'));
    }
  },
  beforeDestroy() {
    stopStream();
  },
  methods: {
    toggleLang() {
      const next = this.$i18n.locale === 'zh' ? 'en' : 'zh';
      this.$i18n.locale = next;
      localStorage.setItem('lang', next);
    },
    goChangePassword() {
      this.$router.push('/change-password');
    },
    goTasks() {
      this.$router.push('/tasks');
    },
    goSecondaryTasks() {
      this.$router.push('/secondary/tasks');
    },
    async logout() {
      stopStream();
      await this.$store.dispatch('logout');
      this.$router.replace('/login');
    }
  }
};
</script>

<style scoped>
.layout {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14px;
  gap: 14px;
  box-sizing: border-box;
}
.topbar {
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}
.brand__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-1), var(--accent-2));
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.18);
}
.brand__title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
}
.topbar__right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.badge {
  margin: 0 6px;
}
.user-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}
.content {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 14px;
}
.sidebar {
  padding: 10px;
}
.menu {
  border-right: 0;
  background: transparent;
}
.el-menu-item {
  position: relative;
  overflow: visible !important;
}
.role-secondary .brand__dot {
  background: linear-gradient(135deg, #8b5cf6, #06b6d4);
}
.menu-item-badge {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  border-radius: 999px;
  background: #f56c6c;
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
  z-index: 2;
  pointer-events: none;
}
.main {
  min-height: 0;
  overflow: auto;
  padding: 0;
}
@media (max-width: 980px) {
  .content {
    grid-template-columns: 1fr;
  }
  .sidebar {
    display: none;
  }
}
</style>
